import { J1TransferarrowB, J2Multiply } from '@e3dc-react/icons'
import { IconButton } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import React from 'react'
import { DASHBOARD_WEATHER_FORECAST_WIDGET_ID } from 'src/Libs/qa/ids'
import { makeStyles } from 'tss-react/mui'
import { WIDGET_HEIGHT, WIDGET_SM_DOWN_MAX_HEIGHT, WIDGET_SM_DOWN_MIN_HEIGHT } from '../base-card/const'
import PvForecastWidgetChart from './PvForecastWidgetChart'
import AnimatedText from 'src/Elements/Utilities/AnimatedText'
import { debounce } from 'lodash'
import type { PvForecastWidgetData } from '../../types'

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    boxShadow: theme.effectStyles.shadowMain,
    minHeight: WIDGET_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 200px',
    background: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    minWidth: 200,
    maxWidth: `calc(25% - ${theme.spacing(1.5)})`,
    alignItems: 'stretch',
    padding: theme.spacing(2),
    gap: theme.spacing(1),

    [theme.breakpoints.down('lg')]: {
      maxWidth: '100%',
    },

    [theme.breakpoints.down('sm')]: {
      flexBasis: '40%',
      minWidth: 'auto',
      maxHeight: WIDGET_SM_DOWN_MAX_HEIGHT,
      minHeight: WIDGET_SM_DOWN_MIN_HEIGHT,
    },
  },
  headerContainer: {
    display: 'flex',
    gap: 2,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    position: 'relative',

    '& > button': {
      padding: 0,
      position: 'absolute',
      top: 0,
      right: 0,
    },
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  subTitle: {
    display: 'flex',
    alignItems: 'center',

    '& > svg': {
      fontSize: 12,
      marginLeft: 0.5,
    },
  },
}))

/*
 * Renders the pv forecast widget which displays the total or remaining pv production for the current day
 */
const PvForecastWidget: React.FC<PvForecastWidgetData> = ({
  pvForecastWidgetValueAndUnit,
  pvForecastWidgetChartData,
  showRemainingProduction,
  isNoDataAvailable,
  handleToggleShowRemainingProduction,
}) => {
  const { classes } = useStyles()

  return (
    <Box className={classes.mainContainer} data-qa={DASHBOARD_WEATHER_FORECAST_WIDGET_ID}>
      <Header
        onButtonClick={handleToggleShowRemainingProduction}
        showRemainingProduction={showRemainingProduction}
        isNoDataAvailable={isNoDataAvailable}
      />
      <Value pvForecastWidgetValueAndUnit={pvForecastWidgetValueAndUnit} isNoDataAvailable={isNoDataAvailable} />
      <PvForecastWidgetChart pvForecastWidgetChartData={pvForecastWidgetChartData} isNoDataAvailable={isNoDataAvailable} />
    </Box>
  )
}

export default PvForecastWidget

const Header: React.FC<{
  showRemainingProduction: boolean
  isNoDataAvailable: boolean
  onButtonClick: () => void
}> = ({ showRemainingProduction, isNoDataAvailable, onButtonClick }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const subTitle = isNoDataAvailable
    ? t('dashboard.liveView.widgets.pvForecast.subtitle.noData')
    : showRemainingProduction
    ? t('dashboard.liveView.widgets.pvForecast.subtitle.remainingProduction')
    : t('dashboard.liveView.widgets.pvForecast.subtitle.totalProduction')

  const debouncedButtonClick = debounce(onButtonClick, 300)

  return (
    <Box className={classes.headerContainer}>
      <Box className={classes.textWrapper}>
        <Typography variant="h3">{t('dashboard.liveView.widgets.pvForecast.title')}</Typography>
        <AnimatedText trigger={subTitle}>
          <Typography variant="caption" whiteSpace={'nowrap'} className={classes.subTitle}>
            {subTitle} {isNoDataAvailable && <J2Multiply />}
          </Typography>
        </AnimatedText>
      </Box>
      {!isNoDataAvailable && <IconButton icon={<J1TransferarrowB />} onClick={debouncedButtonClick} />}
    </Box>
  )
}

const Value: React.FC<{ pvForecastWidgetValueAndUnit: PvForecastWidgetData['pvForecastWidgetValueAndUnit']; isNoDataAvailable: boolean }> = ({
  pvForecastWidgetValueAndUnit,
  isNoDataAvailable,
}) => {
  if (isNoDataAvailable) return null

  return (
    <Typography component="h1">
      <AnimatedText trigger={`${pvForecastWidgetValueAndUnit.value}-${pvForecastWidgetValueAndUnit.unit}`}>
        <Typography variant="h2" component="span">
          ~
        </Typography>
        <Typography variant="h1" component="span">
          {` ${pvForecastWidgetValueAndUnit.value} `}
        </Typography>
        <Typography variant="h2" component="span">
          {pvForecastWidgetValueAndUnit.unit}
        </Typography>
      </AnimatedText>
    </Typography>
  )
}
